import { useState } from "react";
import { Search, Users, BookOpen, Lock, Globe, Star, ChevronDown, Trophy } from "lucide-react";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { useToast } from "../../hooks/use-toast";





const universities = ["All Universities", "ADA University", "Baku State University", "Baku Engineering University", "Khazar University", "ASOIU"];
const majors = ["All Majors", "Computer Science", "Mathematics", "Chemical Engineering", "Physics", "Economics", "Biology"];
const years = ["All Years", "2024", "2025", "2026", "2027"];

const allClasses = [
{ id: 1, name: "CS201 — Data Structures", university: "Baku State University", major: "Computer Science", year: "2025", members: 45, totalPoints: 2450, bio: "Comprehensive study of data structures and algorithms, including arrays, linked lists, trees, graphs, sorting, and dynamic programming.", isPrivate: false, initials: "CS", rating: 4.8 },
{ id: 2, name: "MATH301 — Linear Algebra", university: "ADA University", major: "Mathematics", year: "2025", members: 32, totalPoints: 1890, bio: "Advanced linear algebra covering vector spaces, eigenvalues, matrix decompositions, and applications in data science.", isPrivate: true, initials: "MA", rating: 4.7 },
{ id: 3, name: "CHEM101 — General Chemistry", university: "Baku Engineering University", major: "Chemical Engineering", year: "2026", members: 58, totalPoints: 3200, bio: "Introduction to general chemistry: atomic structure, chemical bonding, thermodynamics, and reaction kinetics.", isPrivate: false, initials: "CH", rating: 4.5 },
{ id: 4, name: "PHYS202 — Electromagnetism", university: "Khazar University", major: "Physics", year: "2025", members: 28, totalPoints: 1560, bio: "Maxwell's equations, electromagnetic waves, optics, and modern physics applications.", isPrivate: true, initials: "PH", rating: 4.6 },
{ id: 5, name: "CS101 — Intro to Programming", university: "ADA University", major: "Computer Science", year: "2026", members: 72, totalPoints: 4100, bio: "Learn Python from scratch — variables, loops, functions, OOP, and file handling.", isPrivate: false, initials: "IP", rating: 4.9 },
{ id: 6, name: "ECON201 — Microeconomics", university: "Baku State University", major: "Economics", year: "2024", members: 40, totalPoints: 2100, bio: "Supply and demand, market structures, consumer behavior, and game theory fundamentals.", isPrivate: false, initials: "EC", rating: 4.4 },
{ id: 7, name: "BIO301 — Molecular Biology", university: "ASOIU", major: "Biology", year: "2025", members: 22, totalPoints: 980, bio: "DNA replication, gene expression, protein synthesis, and molecular genetics techniques.", isPrivate: true, initials: "MB", rating: 4.3 },
{ id: 8, name: "CS301 — Operating Systems", university: "Baku State University", major: "Computer Science", year: "2025", members: 38, totalPoints: 2800, bio: "Process management, memory allocation, file systems, and concurrency in modern operating systems.", isPrivate: false, initials: "OS", rating: 4.7 }];


const MAX_CLASSES = 5;

const ClassesDiscovery = ({ onOpenClass }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUni, setSelectedUni] = useState("All Universities");
  const [selectedMajor, setSelectedMajor] = useState("All Majors");
  const [selectedYear, setSelectedYear] = useState("All Years");
  const [joinedClasses, setJoinedClasses] = useState([1, 2, 3]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [searchMode, setSearchMode] = useState("search");
  const [memberRoles, setMemberRoles] = useState({
    1: { "You": "admin", "Ali A.": "member", "Nigar S.": "moderator" },
    2: { "You": "member", "Prof. Hasanov": "admin" },
    3: { "You": "member", "Dr. Mammadov": "admin" }
  });
  const [showRoleModal, setShowRoleModal] = useState(null);
  const { toast } = useToast();

  const filtered = allClasses.filter((c) => {
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesUni = selectedUni === "All Universities" || c.university === selectedUni;
    const matchesMajor = selectedMajor === "All Majors" || c.major === selectedMajor;
    const matchesYear = selectedYear === "All Years" || c.year === selectedYear;
    return matchesSearch && matchesUni && matchesMajor && matchesYear;
  });

  const handleJoin = (classItem) => {
    if (joinedClasses.includes(classItem.id)) return;
    if (joinedClasses.length >= MAX_CLASSES) {
      toast({ title: "Class limit reached", description: `You can join a maximum of ${MAX_CLASSES} classes. Leave one to join another.`, variant: "destructive" });
      return;
    }
    if (classItem.isPrivate) {
      setPendingRequests((prev) => [...prev, classItem.id]);
      toast({ title: "Join request sent", description: `Your request to join "${classItem.name}" has been sent to the admins.` });
    } else {
      setJoinedClasses((prev) => [...prev, classItem.id]);
      toast({ title: "Joined!", description: `You are now a member of "${classItem.name}".` });
    }
  };

  const getJoinStatus = (classItem) => {
    if (joinedClasses.includes(classItem.id)) return "joined";
    if (pendingRequests.includes(classItem.id)) return "pending";
    return "none";
  };

  const myClasses = allClasses.filter((c) => joinedClasses.includes(c.id));
  const getMyRole = (classId) => memberRoles[classId]?.["You"] || "member";

  const handleRoleChange = (classId, userName, newRole) => {
    setMemberRoles((prev) => ({
      ...prev,
      [classId]: { ...prev[classId], [userName]: newRole }
    }));
    toast({ title: "Role updated", description: `${userName} is now ${newRole}.` });
    setShowRoleModal(null);
  };

  return (
    <div className="space-y-4">
      {/* Search & Filters */}
      <div className="stat-card !p-4 space-y-3">
        <div className="flex gap-1 p-1 rounded-xl bg-muted w-fit">
          <button onClick={() => setSearchMode("search")}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${searchMode === "search" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
            Search Classes
          </button>
          <button onClick={() => setSearchMode("events")}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${searchMode === "events" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
            Find Events
          </button>
          <button onClick={() => setSearchMode("my")}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${searchMode === "my" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
            My Classes
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchMode === "my" ? "Search my classes..." : "Search classes by name..."}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
          
        </div>
        <div className="flex flex-wrap gap-2">
          {/* University filter */}
          <div className="relative">
            <select
              value={selectedUni}
              onChange={(e) => setSelectedUni(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 rounded-xl bg-muted text-xs font-medium text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring">
              
              {universities.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
          </div>
          {/* Major filter */}
          <div className="relative">
            <select
              value={selectedMajor}
              onChange={(e) => setSelectedMajor(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 rounded-xl bg-muted text-xs font-medium text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring">
              
              {majors.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
          </div>
          {/* Year filter */}
          <div className="relative">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 rounded-xl bg-muted text-xs font-medium text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring">
              
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{filtered.length} class{filtered.length !== 1 ? "es" : ""} found</span>
          <span className="flex items-center gap-1">
            <BookOpen className="w-3 h-3" />
            My Classes: {joinedClasses.length}/{MAX_CLASSES}
          </span>
        </div>
      </div>

      {/* Classes Grid */}
      {searchMode === "search" && <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map((c) => {
          const status = getJoinStatus(c);
          return (
            <div key={c.id} className="stat-card !p-4 flex flex-col">
              <div className="flex items-start gap-3 mb-3">
                <Avatar className="w-11 h-11 flex-shrink-0">
                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">{c.initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => onOpenClass(c.id)}
                      className="text-sm font-display font-bold text-foreground hover:text-secondary transition-colors truncate">
                      
                      {c.name}
                    </button>
                    {c.isPrivate ?
                    <Lock className="w-3 h-3 text-muted-foreground flex-shrink-0" /> :

                    <Globe className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                    }
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    <span className="px-1.5 py-0.5 rounded-md bg-primary/10 text-primary font-medium">{c.university}</span>
                    <span className="mx-1">·</span>
                    {c.major}
                    <span className="mx-1">·</span>
                    {c.year}
                  </p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-3 flex-1">{c.bio}</p>

              <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" /> {c.members} members
                </span>
                <span className="flex items-center gap-1">
                  <Trophy className="w-3 h-3 text-chart-4" /> {c.totalPoints.toLocaleString()} pts
                </span>
                <span className="flex items-center gap-0.5 ml-auto">
                  <Star className="w-3 h-3 text-chart-4 fill-chart-4" /> {c.rating}
                </span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => onOpenClass(c.id)}
                  className="flex-1 h-8 rounded-xl bg-muted text-xs font-medium text-foreground hover:bg-muted/80 transition-colors">
                  
                  View
                </button>
                {status === "joined" ?
                <button className="flex-1 h-8 rounded-xl bg-secondary text-secondary-foreground text-xs font-semibold cursor-default">
                    ✓ Joined
                  </button> :
                status === "pending" ?
                <button className="flex-1 h-8 rounded-xl bg-chart-5/15 text-chart-5 text-xs font-semibold cursor-default">
                    ⏳ Pending
                  </button> :

                <button
                  onClick={() => handleJoin(c)}
                  className="flex-1 h-8 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-1">
                  
                    {c.isPrivate ? <><Lock className="w-3 h-3" /> Request</> : "Join"}
                  </button>
                }
              </div>
            </div>);

        })}
      </div>}

      {/* My Classes View */}
      {searchMode === "my" &&
      <div className="space-y-3">
          {myClasses.length === 0 ?
        <div className="stat-card !p-8 text-center">
              <BookOpen className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-sm font-medium text-muted-foreground">You haven't joined any classes yet</p>
            </div> :

        myClasses.filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase())).map((c) => {
          const role = getMyRole(c.id);
          const members = memberRoles[c.id] || {};
          return (
            <div key={c.id} className="stat-card !p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <Avatar className="w-11 h-11 flex-shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">{c.initials}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => onOpenClass(c.id)} className="text-sm font-display font-bold text-foreground hover:text-secondary transition-colors truncate">{c.name}</button>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase ${
                    role === "admin" ? "bg-chart-4/15 text-chart-4" : role === "moderator" ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground"}`
                    }>{role}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{c.university} · {c.major} · {c.year}</p>
                    </div>
                  </div>

                  {role === "admin" &&
              <div className="mt-2 pt-2 border-t border-border">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Members & Roles</p>
                      <div className="space-y-1">
                        {Object.entries(members).filter(([name]) => name !== "You").map(([name, memberRole]) =>
                  <div key={name} className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-muted/50 text-xs">
                            <span className="text-foreground font-medium">{name}</span>
                            <select
                      value={memberRole}
                      onChange={(e) => handleRoleChange(c.id, name, e.target.value)}
                      className="appearance-none bg-transparent text-[10px] font-medium text-muted-foreground cursor-pointer focus:outline-none">
                      
                              <option value="member">Member</option>
                              <option value="moderator">Moderator</option>
                              <option value="admin">Admin</option>
                            </select>
                          </div>
                  )}
                      </div>
                    </div>
              }
                </div>);

        })
        }
        </div>
      }

      {searchMode === "search" && filtered.length === 0 &&
      <div className="stat-card !p-8 text-center">
          <BookOpen className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No classes found</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Try adjusting your filters</p>
        </div>
      }
    </div>);

};

export default ClassesDiscovery;